import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { getAllReceipts } from '../../api/receipts';
import { toast } from 'react-toastify';
import ReceiptPreviewModal from '../../components/receipts/ReceiptPreviewModal';

/**
 * ReceiptHistory Component
 * Displays a searchable history of all receipts with modal preview
 */
const ReceiptHistory = () => {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 20,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  // Modal state
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchReceipts();
  }, [pagination.currentPage, search, startDate, endDate, paymentStatus]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const filters = {
        page: pagination.currentPage,
        limit: pagination.limit,
        search,
        startDate,
        endDate,
        paymentStatus,
      };

      const response = await getAllReceipts(filters);
      setReceipts(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchReceipts();
  };

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPaymentStatus('');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleReceiptClick = (receipt) => {
    setSelectedReceipt(receipt);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedReceipt(null);
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      paid: 'bg-green-100 text-green-600',
      partial: 'bg-yellow-100 text-yellow-600',
      unpaid: 'bg-red-100 text-red-600',
    };
    return badges[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-accent-gold" />
            <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
              Receipt History
            </h1>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
                <input
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder="Search receipt number, customer..."
                  className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                />
              </div>
            </div>

            {/* Payment Status */}
            <div>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
              >
                <option value="">All Payment Status</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 lg:col-span-3">
              <button
                type="button"
                onClick={handleFilterChange}
                className="px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors text-sm"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Receipts List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-accent-gold border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-secondary-gray">Loading receipts...</p>
            </div>
          ) : receipts.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-16 h-16 text-secondary-lightGray mx-auto mb-4" />
              <p className="text-secondary-gray">No receipts found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-primary-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Receipt #
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Items
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-lightGray">
                    {receipts.map((receipt) => (
                      <tr
                        key={receipt.id}
                        onClick={() => handleReceiptClick(receipt)}
                        className="hover:bg-secondary-offWhite transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <span className="font-medium text-accent-gold">
                            {receipt.receipt_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-secondary-gray">
                          {new Date(receipt.sale_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-primary-black text-sm">
                              {receipt.customer_name}
                            </p>
                            <p className="text-xs text-secondary-gray">
                              {receipt.customer_phone}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          {receipt.item_count}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-accent-gold">
                          ₦{parseFloat(receipt.total_amount).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusBadge(
                              receipt.payment_status
                            )}`}
                          >
                            {receipt.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-lightGray bg-secondary-offWhite">
                  <div className="text-sm text-secondary-gray">
                    Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)}{' '}
                    of {pagination.totalRecords} receipts
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          currentPage: prev.currentPage - 1,
                        }))
                      }
                      disabled={pagination.currentPage === 1}
                      className="flex items-center gap-1 px-3 py-1 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          currentPage: prev.currentPage + 1,
                        }))
                      }
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="flex items-center gap-1 px-3 py-1 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Receipt Preview Modal - Uses reusable component for robust PDF rendering */}
      {modalOpen && selectedReceipt && (
        <ReceiptPreviewModal
          saleId={selectedReceipt.id}
          receiptNumber={selectedReceipt.receipt_number}
          customerName={selectedReceipt.customer_name}
          customerPhone={selectedReceipt.customer_phone}
          customerEmail={selectedReceipt.customer_email}
          totalAmount={selectedReceipt.total_amount}
          saleDate={selectedReceipt.sale_date}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default ReceiptHistory;
